/**
 * TasksDashboard.jsx — Iter 207.
 *
 * Task management overview:
 *   • KPI strip (total, open, in-progress, overdue, done-today, effort variance)
 *   • Group-by pivots: Client / Brand / Project / Assignee / Status / Priority /
 *                       Health / Effort bucket / Deadline bucket
 *   • Line charts: Completion trend, Overdue trend, Effort burn, Throughput
 *   • "Send report" quick actions (morning / evening)
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, BarChart, Bar,
} from "recharts";
import {
  Activity, AlertTriangle, CheckCircle2, Clock, Loader2, Send,
  FileDown, ListChecks, Users, Layers, Briefcase, ShieldAlert, Zap,
  Timer, Rocket, RefreshCw, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const RANGES = [
  { d: 7, label: "7d" }, { d: 30, label: "30d" },
  { d: 90, label: "90d" }, { d: 180, label: "6m" },
];

const STATUS_LABEL = {
  open: "Open", in_progress: "In progress",
  blocked: "Blocked", done: "Done",
};

export default function TasksDashboard() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);

  const load = useCallback(() => {
    if (!wsId) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/tasks/analytics?since_days=${range}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [wsId, range]);
  useEffect(() => { load(); }, [load]);

  const sendReport = async (edition) => {
    setSending(edition);
    const tid = toast.loading(`Sending ${edition} report…`);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/task-report/send-now?edition=${edition}`);
      if (data.ok) toast.success(`Report sent to ${data.sent_to.length} recipient(s)`, { id: tid });
      else toast.warning(data.reason || "Not sent — check config", { id: tid });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Send failed"), { id: tid });
    } finally { setSending(null); }
  };

  const previewPdf = async (edition) => {
    // Open a placeholder tab synchronously so Chromium doesn't block it.
    const w = window.open("", "_blank");
    try {
      const resp = await api.post(
        `/workspaces/${wsId}/task-report/preview?edition=${edition}`,
        {}, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      if (w) { w.location.href = url; }
      else { window.location.href = url; }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) { try { w.close(); } catch { /* noop */ } }
      toast.error(apiErrorMessage(e, "Preview failed"));
    }
  };

  const summary = data?.summary;

  return (
    <div className="space-y-6" data-testid="tasks-dashboard-page">
      <div className="flex items-center gap-3 flex-wrap">
        <ListChecks className="w-6 h-6 text-primary" />
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-2xl font-bold">Tasks Dashboard</h1>
          <div className="text-xs text-muted-foreground">
            Executive view of tasks across projects, clients, brands, assignees, health & deadlines.
          </div>
        </div>
        <PersonalDigestButton wsId={wsId} />
        <div className="flex items-center gap-1 border border-border">
          {RANGES.map((r) => (
            <button key={r.d} type="button"
                     onClick={() => setRange(r.d)}
                     className={`px-3 h-8 text-xs ${range === r.d ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                     data-testid={`td-range-${r.d}`}>
              {r.label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" className="rounded-none" onClick={load} disabled={busy}
                data-testid="td-refresh">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="rounded-none"
                   onClick={() => previewPdf("morning")}
                   data-testid="td-preview-morning">
            <FileDown className="w-3 h-3 mr-1" /> Preview PDF
          </Button>
          <Button size="sm" className="rounded-none"
                   onClick={() => sendReport("morning")}
                   disabled={sending === "morning"}
                   data-testid="td-send-morning">
            {sending === "morning" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Morning
          </Button>
          <Button size="sm" className="rounded-none"
                   onClick={() => sendReport("evening")}
                   disabled={sending === "evening"}
                   data-testid="td-send-evening">
            {sending === "evening" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
            Evening
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="td-kpis">
        <KPI icon={ListChecks} label="Total" value={summary?.total ?? "—"} />
        <KPI icon={Activity} label="In progress" value={summary?.in_progress ?? "—"} tone="blue" />
        <KPI icon={Clock} label="Blocked" value={summary?.blocked ?? "—"} tone="amber" />
        <KPI icon={AlertTriangle} label="Overdue" value={summary?.overdue ?? "—"} tone="red" />
        <KPI icon={CheckCircle2} label="Done" value={summary?.done ?? "—"} tone="emerald" />
        <KPI icon={Timer} label="Effort variance"
              value={summary?.effort_variance_hours != null ? `${summary.effort_variance_hours > 0 ? "+" : ""}${summary.effort_variance_hours}h` : "—"}
              tone={summary?.effort_variance_hours > 0 ? "red" : "emerald"} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Completion & new tasks" testid="td-chart-completion">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data?.trend?.completion || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="done" stroke="#059669" strokeWidth={2} dot={false} name="Done" />
              <Line type="monotone" dataKey="opened" stroke="#0369a1" strokeWidth={2} dot={false} name="Opened" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Overdue trend" testid="td-chart-overdue">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data?.trend?.overdue || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="overdue" stroke="#dc2626" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Throughput (tasks done / day)" testid="td-chart-throughput">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data?.trend?.throughput || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="tasks_done" fill="#7c3aed" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Effort burn (hours logged / day)" testid="td-chart-effort">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data?.trend?.effort || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="hours_logged" stroke="#ea580c" strokeWidth={2} dot={false} name="Hours" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Group-by pivots */}
      <Tabs defaultValue="assignee" className="space-y-3">
        <TabsList className="rounded-none">
          <TabsTrigger value="assignee" data-testid="td-tab-assignee"><Users className="w-3 h-3 mr-1" />Assignee</TabsTrigger>
          <TabsTrigger value="client" data-testid="td-tab-client"><Briefcase className="w-3 h-3 mr-1" />Client</TabsTrigger>
          <TabsTrigger value="brand" data-testid="td-tab-brand"><Layers className="w-3 h-3 mr-1" />Brand</TabsTrigger>
          <TabsTrigger value="project" data-testid="td-tab-project"><Rocket className="w-3 h-3 mr-1" />Project</TabsTrigger>
          <TabsTrigger value="status" data-testid="td-tab-status">Status</TabsTrigger>
          <TabsTrigger value="priority" data-testid="td-tab-priority"><Zap className="w-3 h-3 mr-1" />Priority</TabsTrigger>
          <TabsTrigger value="health" data-testid="td-tab-health"><ShieldAlert className="w-3 h-3 mr-1" />Health</TabsTrigger>
          <TabsTrigger value="effort" data-testid="td-tab-effort">Effort</TabsTrigger>
          <TabsTrigger value="deadline" data-testid="td-tab-deadline">Deadline</TabsTrigger>
        </TabsList>
        <TabsContent value="assignee"><GroupTable rows={data?.by_assignee} keyName="assignee_name" testid="td-group-assignee" /></TabsContent>
        <TabsContent value="client"><GroupTable rows={data?.by_client} keyName="client_name" testid="td-group-client" /></TabsContent>
        <TabsContent value="brand"><GroupTable rows={data?.by_brand} keyName="brand_name" testid="td-group-brand" /></TabsContent>
        <TabsContent value="project"><GroupTable rows={data?.by_project} keyName="project_name" testid="td-group-project" /></TabsContent>
        <TabsContent value="status"><SimpleGroup rows={data?.by_status} testid="td-group-status" /></TabsContent>
        <TabsContent value="priority"><SimpleGroup rows={data?.by_priority} testid="td-group-priority" /></TabsContent>
        <TabsContent value="health"><SimpleGroup rows={data?.by_health} testid="td-group-health" /></TabsContent>
        <TabsContent value="effort"><SimpleGroup rows={data?.by_effort_bucket} testid="td-group-effort" /></TabsContent>
        <TabsContent value="deadline"><SimpleGroup rows={data?.by_deadline_bucket} testid="td-group-deadline" /></TabsContent>
      </Tabs>
    </div>
  );
}


function KPI({ icon: Icon, label, value, tone }) {
  const cls = {
    blue: "text-blue-700 border-blue-500/30",
    amber: "text-amber-700 border-amber-500/30",
    red: "text-red-700 border-red-500/30",
    emerald: "text-emerald-700 border-emerald-500/30",
  }[tone] || "text-foreground border-border";
  return (
    <div className={`border p-3 flex items-start gap-2 ${cls}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <div>
        <div className="text-2xl font-bold leading-none">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}


function ChartCard({ title, testid, children }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">{title}</div>
      {children}
    </div>
  );
}


function GroupTable({ rows, keyName, testid }) {
  if (!rows) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
  if (rows.length === 0) return <div className="text-sm text-muted-foreground italic p-4">No data.</div>;
  return (
    <div className="border border-border overflow-auto max-h-[500px]" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-muted/40 sticky top-0">
          <tr>
            <th className="text-left px-2 py-1.5 font-semibold">Name</th>
            <th className="text-right px-2 py-1.5 font-semibold">Total</th>
            <th className="text-right px-2 py-1.5 font-semibold text-blue-700">In prog</th>
            <th className="text-right px-2 py-1.5 font-semibold text-amber-700">Blocked</th>
            <th className="text-right px-2 py-1.5 font-semibold text-emerald-700">Done</th>
            <th className="text-right px-2 py-1.5 font-semibold text-red-700">Overdue</th>
            <th className="text-right px-2 py-1.5 font-semibold">Est h</th>
            <th className="text-right px-2 py-1.5 font-semibold">Logged h</th>
            <th className="text-right px-2 py-1.5 font-semibold">Δ h</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border hover:bg-muted/20">
              <td className="px-2 py-1.5 truncate max-w-[240px]">{r[keyName]}</td>
              <td className="px-2 py-1.5 text-right font-semibold">{r.total}</td>
              <td className="px-2 py-1.5 text-right text-blue-700">{r.in_progress || 0}</td>
              <td className="px-2 py-1.5 text-right text-amber-700">{r.blocked || 0}</td>
              <td className="px-2 py-1.5 text-right text-emerald-700">{r.done || 0}</td>
              <td className="px-2 py-1.5 text-right text-red-700">{r.overdue || 0}</td>
              <td className="px-2 py-1.5 text-right">{r.est_hours ?? "—"}</td>
              <td className="px-2 py-1.5 text-right">{r.logged_hours ?? "—"}</td>
              <td className={`px-2 py-1.5 text-right font-semibold ${(r.effort_variance_hours || 0) > 0 ? "text-red-700" : "text-emerald-700"}`}>
                {r.effort_variance_hours != null ? `${r.effort_variance_hours > 0 ? "+" : ""}${r.effort_variance_hours}` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function SimpleGroup({ rows, testid }) {
  if (!rows) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
  if (rows.length === 0) return <div className="text-sm text-muted-foreground italic p-4">No data.</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.key} className="border border-border p-3">
          <div className="text-sm font-semibold">{r.key}</div>
          <div className="text-2xl font-bold">{r.total}</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
            {r.done || 0} done · {r.overdue || 0} overdue
          </div>
        </div>
      ))}
    </div>
  );
}


// ─── Personal Digest (Iter 208) ────────────────────────────────────

function PersonalDigestButton({ wsId }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/task-report/personal-config`)
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg({ enabled: false, morning_time: "09:00",
        evening_time: "18:00", timezone: "UTC",
        morning_enabled: true, evening_enabled: true }));
  }, [wsId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${wsId}/task-report/personal-config`, cfg);
      toast.success(cfg?.enabled
        ? "Personal digest enabled" : "Personal digest disabled");
      setOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const sendNow = async (edition) => {
    setSending(true);
    const tid = toast.loading(`Sending your ${edition} digest…`);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/task-report/personal-send-now?edition=${edition}`);
      if (data.ok) toast.success(`Sent to you`, { id: tid });
      else toast.warning(data.reason || "Delivery skipped", { id: tid });
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed"), { id: tid }); }
    finally { setSending(false); }
  };

  const previewPdf = async () => {
    const w = window.open("", "_blank");
    try {
      const resp = await api.post(
        `/workspaces/${wsId}/task-report/personal-preview?edition=morning`,
        {}, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      if (w) w.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) { try { w.close(); } catch { /* noop */ } }
      toast.error(apiErrorMessage(e, "Preview failed"));
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none"
               onClick={() => setOpen(true)} data-testid="td-personal-btn">
        <Users className="w-3 h-3 mr-1" /> My digest
      </Button>
      {open && cfg && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
             onClick={() => setOpen(false)} data-testid="td-personal-dialog">
          <div className="bg-card border border-border max-w-md w-full"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-border font-semibold text-sm">
              My personal daily digest
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="text-[11px] text-muted-foreground">
                Get a daily email with just YOUR tasks — filtered by tasks assigned to you.
                Runs on your local timezone.
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!cfg.enabled}
                        onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                        data-testid="td-personal-enabled" />
                <span className="text-sm">Enable my digest</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs">Timezone
                  <input value={cfg.timezone}
                          onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })}
                          className="w-full h-8 border border-border bg-background px-2 mt-0.5 text-xs"
                          data-testid="td-personal-tz" />
                </label>
                <div />
                <label className="text-xs flex flex-col">
                  <span className="flex items-center gap-1">
                    <input type="checkbox" checked={cfg.morning_enabled !== false}
                            onChange={(e) => setCfg({ ...cfg, morning_enabled: e.target.checked })} />
                    Morning
                  </span>
                  <input type="time" value={cfg.morning_time}
                          onChange={(e) => setCfg({ ...cfg, morning_time: e.target.value })}
                          className="h-8 border border-border bg-background px-2 mt-1 text-xs"
                          data-testid="td-personal-morning-time" />
                </label>
                <label className="text-xs flex flex-col">
                  <span className="flex items-center gap-1">
                    <input type="checkbox" checked={cfg.evening_enabled !== false}
                            onChange={(e) => setCfg({ ...cfg, evening_enabled: e.target.checked })} />
                    Evening
                  </span>
                  <input type="time" value={cfg.evening_time}
                          onChange={(e) => setCfg({ ...cfg, evening_time: e.target.value })}
                          className="h-8 border border-border bg-background px-2 mt-1 text-xs"
                          data-testid="td-personal-evening-time" />
                </label>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button size="sm" variant="outline" className="rounded-none"
                         onClick={previewPdf} data-testid="td-personal-preview">
                  <FileDown className="w-3 h-3 mr-1" /> Preview
                </Button>
                <Button size="sm" variant="outline" className="rounded-none"
                         onClick={() => sendNow("morning")}
                         disabled={sending} data-testid="td-personal-send-morning">
                  <Send className="w-3 h-3 mr-1" /> Test morning
                </Button>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border flex items-center gap-2 justify-end">
              <Button size="sm" variant="outline" className="rounded-none"
                       onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" className="rounded-none" onClick={save}
                       disabled={saving} data-testid="td-personal-save">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
